import { sendErrorEvent, NamespaceLoggerAdapter, isContainerUpgradeRequiredError } from '@fluidx/utilities';
import { ITelemetryProperties } from '@fluidframework/common-definitions';
import { ITelemetryNamespaceLogger } from '@fluidx/office-fluid-types';
import { DriverErrorType } from '@fluidframework/driver-definitions';
import { OdspErrorType } from '@fluidframework/odsp-driver-definitions';

const chunkLoadErrorRegex = 'Loading chunk\\s[a-zA-Z0-9_-]+\\sfailed';

/**
 * Method that logs all the QoS Errors to a specific table
 * @param logger Logger instance to use for logging
 * @param eventName eventName to provide to the log statement
 * @param pillar The ASHA pillar a particular error corresponds to, like Open, Create etc
 * @param veto The ASHA veto a specific error corresponds to, like FileOpenFailed, FileCreateFailed etc
 * @param errorType A string that uniquely identifies the error. This is different from eventName since an eventName can encompass a
 * number of errorTypes, for example an eventName like LoadComponentInIframe can encompass errorTypes like IframeTimeoutFailure, FileNotFound etc
 * @param additionalProps Optional additional properties to log with the event
 * @param error The exact error the occurs
 */
export function logQoSError(
  logger: ITelemetryNamespaceLogger,
  eventName: string,
  pillar: string,
  veto: string,
  errorType: string,
  additionalProps?: ITelemetryProperties,
  error?: any
): void {
  // Creates a table Office_Fluid_QoS_Error that encorporates all QoS Errors
  const loggerInstance = new NamespaceLoggerAdapter('QoS', logger);

  sendErrorEvent(
    loggerInstance,
    {
      eventName,
      pillar,
      veto,
      errorType,
      isAce: !isIceError(error),
      ...additionalProps
    },
    error
  );
}

/**
 * Returns true if the error is an ICE error
 */
export function isIceError(error: any): boolean {
  // If error is undefined, categorize as ACE
  if (!error) {
    return false;
  }

  if (
    (error.errorType &&
      [
        DriverErrorType.offlineError,
        DriverErrorType.fileNotFoundOrAccessDeniedError,
        DriverErrorType.authorizationError,
        OdspErrorType.snapshotTooBig,
        OdspErrorType.invalidFileNameError,
        DriverErrorType.fileOverwrittenInStorage
      ].includes(error.errorType)) ||
    isContainerUpgradeRequiredError(error)
  ) {
    return true;
  }

  // If the error is a chunk load failure, treat as ICE since those generally mean offline scenario
  if (error.message && new RegExp(chunkLoadErrorRegex).test(error.message)) {
    return true;
  }

  return false;
}
